import {
  type Branded,
  type DeviceViewSettingsTable,
  type DeviceViewTablesByNodeType,
  type EdgeDirection,
  type EdgeEntityData,
  type EdgeId,
  type EdgeLabels,
  type Filters,
  type LabelTargetType,
  type NodeGraphExtra,
  type NodeId,
  type NodeSn,
  type NodeType,
  type Packet,
  type ProtocolEdgeType,
  type ReportSeverity,
  type TableColumnSort,
  type TrafficScore,
  brand,
} from '@ipfabric/utils';
import type {
  DebugLogEntry,
  DebugPathlookupDevices,
} from '@ipfabric/utils/pathLookup/DebugPathlookup';
import type { CloudInstanceDetailData } from 'components/DeviceView/CloudConstructView/CloudInstanceDetailContent';
import type { CloudInternetGatewayDetailData } from 'components/DeviceView/CloudConstructView/CloudInternetGatewayDetailContent';
import type { CloudNatGatewayDetailData } from 'components/DeviceView/CloudConstructView/CloudNatGatewayDetailContent';
import type { CloudNetworkDetailData } from 'components/DeviceView/CloudConstructView/CloudNetworkDetailContent';
import type { CloudNetworkInterfaceDetailData } from 'components/DeviceView/CloudConstructView/CloudNetworkInterfaceDetailContent';
import type { CloudPeeringDetailData } from 'components/DeviceView/CloudConstructView/CloudPeeringDetailContent';
import type { CloudSubnetDetailData } from 'components/DeviceView/CloudConstructView/CloudSubnetDetailContent';
import type { DeviceDetailData } from 'containers/HostnameDetail/HostnameDetailContent';

// Table url in browser
type WebEndpoint = string;

export interface ConfigurationData {
  hash: string | null;
  hostname: string | null;
  lastChangeAt: number | null;
  sn: string | null;
}

export type CompareMode = 'added' | 'removed' | 'untouched';

export type DeviceViewLabelsTab = {
  columnKey: string;
  id: string;
  type: LabelTargetType;
  title: string;
};

export interface NodeDetailArgs {
  compareMode?: CompareMode | null;
  deviceId?: NodeId;
  extra: NodeGraphExtra | null;
  intentCheckResult?: ReportSeverity | null;
  isPathLookupNode?: boolean;
  labelsTabs?: DeviceViewLabelsTab[];
  nodeHasPathInspectorDetail?: boolean;
  nodeType: NodeType | null;
  parentNodeSn?: NodeSn | null;
  parentNodeType?: NodeType | null;
  redirectToTable?: WebEndpoint;
  sn: NodeSn;
  title: string;
}

export interface SelectedTable {
  label: string;
  path: string;
  webEndpoint: string;
}

export type SelectedTables = SelectedTable[];

export interface TableFilters {
  [tableName: string]: Filters;
}

export interface TableSort {
  [tableName: string]: TableColumnSort;
}

/**
 * This should be branded this way too, because:
 *
 * * Union DeviceViewEntityId over branded strings | plain literal is glitchy,
 *   e.g. [key in DeviceViewEntityId] then explicitly expects 'packetDetails' key.
 * * It prevents hidden errors by passing edge id / node sn from/to unbranded string
 *   that can be mistaken with 'packetDetails' literal.
 * * I tried Branded<'packetDetails', *> but that twisted Typescript's mind too much
 *   and just produced several false errors.
 */
export type DeviceViewPacketDetailsEntityId = Branded<
  string,
  'DeviceViewPacketDetailsEntityId'
>;
export const deviceViewPacketDetailsEntityId =
  brand<DeviceViewPacketDetailsEntityId>('packetDetails');
export type DeviceViewPathlookupDebugEntityId = Branded<
  string,
  'DeviceViewPathlookupDebugEntityId'
>;
export const deviceViewPathlookupDebugEntityId =
  brand<DeviceViewPathlookupDebugEntityId>('pathlookupDebug');
export type DeviceViewNodeId = Branded<string, 'DeviceViewNodeId'>;
export type DeviceViewEdgeId = Branded<string, 'DeviceViewEdgeId'>;
export type DeviceViewLabelsEntityId = Branded<
  string,
  'DeviceViewLabelsEntityId'
>;

export type DeviceViewElementType =
  | 'edge'
  | 'labels'
  | 'node'
  | 'packetDetails'
  | 'pathlookupDebug';

export type DeviceViewEntityId =
  | DeviceViewNodeId
  | DeviceViewEdgeId
  | DeviceViewLabelsEntityId
  | DeviceViewPacketDetailsEntityId
  | DeviceViewPathlookupDebugEntityId;

interface AbstractDeviceViewEntity {
  deviceViewElement: DeviceViewElementType;
  id: DeviceViewEntityId;
  table: string | null;
  tableFilters: TableFilters;
  tableSort: TableSort;
}

export interface DeviceViewNodeEntity extends AbstractDeviceViewEntity {
  compareMode?: CompareMode | null;
  deviceId?: NodeId;
  extra: NodeGraphExtra | null;
  id: DeviceViewNodeId;
  nodeType: NodeType | null;
  deviceViewElement: 'node';
  intentCheckResult?: ReportSeverity | null;
  isPathLookupNode: boolean;
  labelsTabs: DeviceViewLabelsTab[];
  nodeHasPathInspectorDetail: boolean;
  /**
   * Every node entity can have preselected table. Otherwise default tab is opened.
   * Tab cannot be opened before settings with preconfigured tables has been loaded.
   * All redirects are saved here and cleaned when settings is loaded.
   * The reason to wait is that command 'showTable' will automatically find selected table from settings
   * or add selected table to settings.
   */
  redirectToTable?: WebEndpoint;
  sn: NodeSn;
  parentNodeSn?: NodeSn | null;
  parentNodeType?: NodeType | null;
  title: string;
}

export interface DeviceViewLabelsEntity extends AbstractDeviceViewEntity {
  deviceViewElement: 'labels';
  id: DeviceViewLabelsEntityId;
  labelsTabs: DeviceViewLabelsTab[];
  title: string;
}

export interface DeviceExplorerEdgeData {
  color: number | undefined;
  compareMode?: CompareMode | null;
  children: EdgeId[];
  edgeDirection: EdgeDirection;
  intentCheckResult?: ReportSeverity | null;
  labels: string[];
  protocol?: ProtocolEdgeType;
  sourceIfaceName: string | null;
  sourceLabel: string | null;
  sourceSn: NodeSn | null;
  sourceType: NodeType | null;
  targetIfaceName: string | null;
  targetLabel: string | null;
  targetSn: NodeSn | null;
  targetType: NodeType | null;
}

export type DeviceViewEdgeEntity = AbstractDeviceViewEntity &
  DeviceExplorerEdgeData & {
    deviceViewElement: 'edge';
    edges?: EdgeEntityData[];
    edgeId: EdgeId;
    id: DeviceViewEdgeId;
    loaded: boolean;
    // Wait until edge settings is loaded. Then table can be selected, redirected from default tab to desired table
    redirectToTable?: WebEndpoint;
    type: 'topology' | 'pathLookup';
  };

export type PathLookupPathInspectorSelectedPacketSource =
  | 'edge'
  | 'acceptedPackets'
  | 'droppedPackets'
  | 'generatedPackets';

export interface PathLookupPathInspectorSelectedPacket {
  selectedIncomingPacket: PacketDetail | null;
  selectedIncomingPacketSource: PathLookupPathInspectorSelectedPacketSource | null;
  selectedOutgoingPacket: PacketDetail | null;
  selectedOutgoingPacketSource: PathLookupPathInspectorSelectedPacketSource | null;
  successorPacketId?: EdgeId;
}

export type PathLookupPathInspectorSelectedPackets = {
  [Key in NodeSn]: PathLookupPathInspectorSelectedPacket;
};

export interface PathInspectorPacket {
  edgeDirection?: EdgeDirection;
  id: EdgeId;
  labels?: EdgeLabels;
  packet: Packet;
  // Only for incoming packets. Terminated packets don't show this info.
  severityInfo?: Record<ReportSeverity, number>;
  sourceLabel?: string | null;
  targetLabel?: string | null;
  trafficScore?: TrafficScore;
}

export interface PathInspectorNodePackets {
  acceptedPackets: PathInspectorPacket[];
  droppedPackets: PathInspectorPacket[];
  generatedPackets: PathInspectorPacket[];
  edgePackets: PathInspectorPacket[];
}

export type PathInspectorPackets = {
  [Key in NodeSn]: PathInspectorNodePackets;
};

export interface PacketDetail {
  edgeDirection?: EdgeDirection;
  hasNextPathLookupNode?: boolean;
  hasPreviousPathLookupNode?: boolean;
  id: EdgeId;
  labels?: EdgeLabels;
  packet: Packet;
  showAnimation: boolean;
  sourceLabel: string | null;
  targetLabel: string | null;
  trafficScore?: TrafficScore;
}

export interface DeviceViewPacketDetailsEntity {
  deviceViewElement: 'packetDetails';
  id: DeviceViewPacketDetailsEntityId;
  details: PacketDetail[];
}

export interface DeviceViewPathlookupDebugEntity {
  deviceViewElement: 'pathlookupDebug';
  id: DeviceViewPathlookupDebugEntityId;
  entries: DebugLogEntry[];
  debugDevices: DebugPathlookupDevices;
}

export const isPacketDetailsEntity = (
  deviceViewEntity: DeviceViewEntity,
): deviceViewEntity is DeviceViewPacketDetailsEntity =>
  deviceViewEntity.deviceViewElement === 'packetDetails';

export const isPathlookupDebugEntity = (
  deviceViewEntity: DeviceViewEntity,
): deviceViewEntity is DeviceViewPathlookupDebugEntity =>
  deviceViewEntity.deviceViewElement === 'pathlookupDebug';

export const isEdgeEntity = (
  deviceViewEntity: DeviceViewEntity,
): deviceViewEntity is DeviceViewEdgeEntity =>
  deviceViewEntity.deviceViewElement === 'edge';

export const isNodeEntity = (
  deviceViewEntity?: DeviceViewEntity,
): deviceViewEntity is DeviceViewNodeEntity =>
  deviceViewEntity?.deviceViewElement === 'node';

export const isLabelsEntity = (
  deviceViewEntity?: DeviceViewEntity,
): deviceViewEntity is DeviceViewLabelsEntity =>
  deviceViewEntity?.deviceViewElement === 'labels';

export type DeviceViewTitle =
  | DeviceViewEdgeEntity['labels']
  | DeviceViewLabelsEntity['title']
  | DeviceViewNodeEntity['title'];

export type DeviceViewEntity =
  | DeviceViewNodeEntity
  | DeviceViewLabelsEntity
  | DeviceViewEdgeEntity
  | DeviceViewPacketDetailsEntity
  | DeviceViewPathlookupDebugEntity;

export type DeviceViewEntities = DeviceViewEntity[];

export interface DeviceViewWindow {
  beforeMaximizeHeight: number;
  beforeMaximizeWidth: number;
  beforeMaximizeX: number;
  beforeMaximizeY: number;
  height: number;
  id: DeviceViewEntityId;
  maximize: boolean;
  width: number;
  x: number;
  y: number;
  zIndex: number;
}

export type DeviceViewWindows = DeviceViewWindow[];

export interface CloseDeviceViewEntityEvent {
  currentDeviceViewElement: DeviceViewElementType;
  currentDeviceViewEntityId: DeviceViewEntityId;
  followingDeviceViewElement: DeviceViewElementType;
  followingDeviceViewEntityId: DeviceViewEntityId;
}

export type DetachedDeviceViewEntities = DeviceViewEntityId[];

export interface DeviceViewSettings {
  edges: {
    [Protocol in ProtocolEdgeType]?: DeviceViewSettingsTable[];
  };
  nodes: DeviceViewTablesByNodeType;
  // Nodes settings for all device types is loaded every time panel is opened. We keep info in device explorer settings.
  // Whereas edge settings must be loaded every time edge is opened. Edge loaded info is in DeviceViewEdgeEntity.
  nodesLoaded: boolean;
  saving: boolean;
}

export interface PathsWithName {
  [apiEndpoint: string]: string;
}

export interface TabData {
  device?: DeviceDetailData;
  cloudNetwork?: CloudNetworkDetailData;
  cloudNetworkInterface?: CloudNetworkInterfaceDetailData;
  cloudSubnet?: CloudSubnetDetailData;
  cloudPeering?: CloudPeeringDetailData;
  cloudInstance?: CloudInstanceDetailData;
  cloudNatGateway?: CloudNatGatewayDetailData;
  cloudInternetGateway?: CloudInternetGatewayDetailData;
  configuration?: ConfigurationData;
}

export type TabDataTables = {
  [DeviceViewEntityIdNode in DeviceViewEntityId]: TabData;
};

export interface PathLookupPacketDiff {
  added: {
    [index: number]: { [key: string]: string | number[] };
  };
  deleted: {
    [index: number]: { [key: string]: string | number[] | undefined };
  };
  updated: {
    [index: number]: { [key: string]: string | number[] };
  };
}

export interface DeviceViewEntitiesUpdateEvent {
  activeDeviceViewEntityId: DeviceViewEntityId;
  activeDeviceViewEntityElement: DeviceViewElementType;
  deviceViewEntities: DeviceViewEntities;
}

export interface RefreshDeviceViewEntityIdsCommand {
  updatedEdgeData: Map<EdgeId, DeviceExplorerEdgeData>;
  edgeIds: EdgeId[];
  deviceSns: NodeSn[];
  onDeviceViewEntitiesUpdate: (event: DeviceViewEntitiesUpdateEvent) => void;
  updatedNodeData: Map<NodeSn, NodeDetailArgs>;
}
